import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { WriteOffStock } from '../../api/dto';
import { writeOffStockSchema } from '../../utils/zodSchemas';
import { useWriteOffStock } from '../../api/queries';
import { fr } from '../../i18n/fr';
import ProductSelectorField from './fields/ProductSelectorField';
import { ProductSelectionItem } from '../../types';

interface Props {
  storeId: string;
  initialProductId?: string;
  initialProduct?: ProductSelectionItem | null;
  onSubmitted?: () => void;
}

const WriteOffForm: React.FC<Props> = ({
  storeId,
  initialProductId,
  initialProduct,
  onSubmitted,
}) => {
  const mutation = useWriteOffStock();
  const defaultValues = useMemo(
    () => ({
      store_id: storeId,
      product_id: initialProductId ?? '',
      quantity: 1,
      to_state: 'lost' as const,
    }),
    [storeId, initialProductId]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<WriteOffStock>({
    resolver: zodResolver(writeOffStockSchema),
    defaultValues,
  });

  useEffect(() => {
    setValue('store_id', storeId);
  }, [setValue, storeId]);

  useEffect(() => {
    setValue('product_id', initialProductId ?? '');
  }, [initialProductId, setValue]);

  const onSubmit = (values: WriteOffStock) => {
    const payload: WriteOffStock = {
      ...values,
      product_id: values.product_id.trim(),
    };

    mutation.mutate(payload, {
      onSuccess: () => {
        reset({
          ...defaultValues,
          to_state: values.to_state,
          quantity: values.quantity,
        });
        onSubmitted?.();
      },
    });
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      aria-label="writeoff-form"
    >
      <input type="hidden" {...register('store_id')} />
      {mutation.isError && (
        <Alert severity="error">{(mutation.error as Error).message}</Alert>
      )}
      <ProductSelectorField
        control={control}
        name="product_id"
        label={fr.product}
        error={!!errors.product_id}
        helperText={errors.product_id?.message}
        initialSelection={initialProduct ?? null}
      />
      <TextField select label="Vers l'état" {...register('to_state')} fullWidth>
        <MenuItem value="lost">Perdu</MenuItem>
        <MenuItem value="damaged">Endommagé</MenuItem>
      </TextField>
      <TextField
        type="number"
        label={fr.quantity}
        inputProps={{ min: 0, step: 1 }}
        {...register('quantity', { valueAsNumber: true })}
        error={!!errors.quantity}
        helperText={errors.quantity?.message}
        fullWidth
      />
      <Button type="submit" variant="contained" disabled={mutation.isPending}>
        {fr.saveMovement}
      </Button>
    </Stack>
  );
};

export default WriteOffForm;
